import { proposalQueryKeys } from "@platform/mpx-quotes-grid";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { RiderCharges } from "@platform/btx-rates";
import type { Proposal, ProposalScope } from "@platform/mpx-quotes-grid";
import type { AxiosInstance } from "axios";

/** Request body for PATCH /btx/v1/proposal-plans/:id */
interface UpdateProposalPlanRiderChargesRequest {
  riderCharges: RiderCharges;
}

const patchProposalPlanRiderCharges = async (
  axiosInstance: AxiosInstance,
  planId: string,
  riderCharges: RiderCharges,
): Promise<void> => {
  const body: UpdateProposalPlanRiderChargesRequest = { riderCharges };
  await axiosInstance.patch(`btx/v1/proposal-plans/${planId}`, body);
};

const hasAnyCharge = (charges: RiderCharges): boolean =>
  [charges.employee, charges.spouse, charges.child].some(
    (charge) => typeof charge === "number",
  );

/** Applies a patch to a plan's cached charges, as the API will: a null clears a charge. */
const applyRiderChargePatch = (
  current: RiderCharges | undefined,
  patch: RiderCharges,
): RiderCharges | undefined => {
  const merged = { ...current, ...patch };
  return hasAnyCharge(merged) ? merged : undefined;
};

interface UseProposalPlanRiderChargesMutationOptions {
  rfpId: string;
  axiosInstance: AxiosInstance;
  /** The proposals read scope (ADR-031) rendered in the grid: the optimistic
   * cache write needs its exact key, while cancel/invalidate prefix-match
   * every scope through `.all`. */
  scope: ProposalScope;
  onSaveError?: (err: Error) => void;
}

/**
 * Saves a Critical Illness proposal plan's rider charges (the rows above the
 * premiums in the rates table). The PATCH carries only the charges the caller
 * passes, so an edit never overwrites a charge someone else changed.
 */
export const useProposalPlanRiderChargesMutation = ({
  rfpId,
  axiosInstance,
  scope,
  onSaveError,
}: UseProposalPlanRiderChargesMutationOptions): {
  saveRiderCharges: (planId: string, riderCharges: RiderCharges) => void;
} => {
  const queryClient = useQueryClient();
  const queryKey = proposalQueryKeys.all(rfpId);
  const scopedQueryKey = proposalQueryKeys.scoped(rfpId, scope);

  const { mutate } = useMutation({
    mutationFn: ({
      planId,
      riderCharges,
    }: {
      planId: string;
      riderCharges: RiderCharges;
    }) => patchProposalPlanRiderCharges(axiosInstance, planId, riderCharges),
    onMutate: async ({
      planId,
      riderCharges,
    }: {
      planId: string;
      riderCharges: RiderCharges;
    }) => {
      await queryClient.cancelQueries({ queryKey });

      const previousProposals =
        queryClient.getQueryData<Proposal[]>(scopedQueryKey);

      queryClient.setQueryData<Proposal[]>(scopedQueryKey, (old) =>
        old?.map((proposal) => ({
          ...proposal,
          coverages: proposal.coverages.map((coverage) => ({
            ...coverage,
            planGroups: coverage.planGroups.map((group) => ({
              ...group,
              plans: group.plans.map((plan) =>
                plan.id === planId
                  ? {
                      ...plan,
                      // The API omits riderCharges when none is set; mirror that.
                      riderCharges: applyRiderChargePatch(
                        plan.riderCharges,
                        riderCharges,
                      ),
                    }
                  : plan,
              ),
            })),
          })),
        })),
      );

      // Rollback targets the scope that was patched, which can differ from
      // the current one if the scope changed while the save was in flight.
      return { previousProposals, scopedQueryKey };
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey, refetchType: "none" }),
    onError: (err: Error, _vars, context) => {
      if (context?.previousProposals !== undefined) {
        queryClient.setQueryData(
          context.scopedQueryKey,
          context.previousProposals,
        );
      }
      onSaveError?.(err);
    },
  });

  const saveRiderCharges = useCallback(
    (planId: string, riderCharges: RiderCharges) =>
      mutate({ planId, riderCharges }),
    [mutate],
  );

  return { saveRiderCharges };
};
